import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Button from "@shared/v2/Button";
import Alert from "@shared/v2/Alert";
import CallBar from "./components/CallBar";
import useDialer from "./hooks/useDialer";
import Loading from "../Goals/components/Loader";
import CallOutcome from "./components/CallOutcome";
import CallNotes from "./components/CallNotes";
import CallButton from "./components/CallButton";
import SinglePerson from "./components/SinglePerson";
import PlatformMessage from "./components/PlatformMessage";

import {
  DIALER_INLINE_NAME,
  checkValidLeads,
  createDialerSession,
  endCall,
  getPersonAndNumbers,
  logCall,
  startRecording,
  stopRecording,
  // non-async
  actionCableUpdateReceivedInline,
  setCallStateInline,
  setPhoneNumberInline,
  clearSessionInline,
  setCallOutcomeInline,
  setCommentInline,
  setCallParamsInline,
  setDurationInline,
} from "../reducers/dialerReducer/dialerReducer";

import {
  useCallOutcome,
  useCallState,
  useConference,
  useError,
  useLoading,
  usePerson,
  usePhoneNumber,
  usePhoneNumbers,
  useSession,
  useValidLeads,
  useIsRecording,
  useComment,
  useIsRecordingConsentRequired,
} from "../reducers/dialerReducer/selectors";

import { DISABLED_CALL_STATES } from "./shared";
import RecordConfirmationModal from "./components/RecordConfirmationModal";

// To use pass in the leadUuid prop containing a person uuid
const SingleDialer = ({ leadUuid }) => {
  const leadUuids = useMemo(() => [leadUuid], [leadUuid]);

  const isRecordingConsentRequired = useIsRecordingConsentRequired(DIALER_INLINE_NAME);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const callOutcome = useCallOutcome(DIALER_INLINE_NAME);
  const callState = useCallState(DIALER_INLINE_NAME);
  const isRecording = useIsRecording(DIALER_INLINE_NAME);
  const conference = useConference(DIALER_INLINE_NAME);
  const error = useError(DIALER_INLINE_NAME);
  const loading = useLoading(DIALER_INLINE_NAME);
  const person = usePerson(DIALER_INLINE_NAME);
  const note = useComment(DIALER_INLINE_NAME);
  const phoneNumber = usePhoneNumber(DIALER_INLINE_NAME);
  const phoneNumbers = usePhoneNumbers(DIALER_INLINE_NAME);
  const session = useSession(DIALER_INLINE_NAME);
  const validLeads = useValidLeads(DIALER_INLINE_NAME);

  const strtRec = startRecording(DIALER_INLINE_NAME);
  const stpRec = stopRecording(DIALER_INLINE_NAME);

  const { actions, disconnectDialer } = useDialer({
    actions: {
      checkValidLeads: checkValidLeads(DIALER_INLINE_NAME),
      createDialerSession: createDialerSession(DIALER_INLINE_NAME),
      endCall: endCall(DIALER_INLINE_NAME),
      getPersonAndNumbers: getPersonAndNumbers(DIALER_INLINE_NAME),
      logCall: logCall(DIALER_INLINE_NAME),
      startRecording: strtRec,
      stopRecording: stpRec,
      actionCableUpdateReceived: actionCableUpdateReceivedInline,
      setCallState: setCallStateInline,
      setPhoneNumber: setPhoneNumberInline,
      clearSession: clearSessionInline,
      setCallOutcome: setCallOutcomeInline,
      setComment: setCommentInline,
      setCallParams: setCallParamsInline,
    },
    selectors: {
      callOutcome,
      callState,
      conference,
      leadUuids,
      loading,
      person,
      phoneNumber,
      phoneNumbers,
      session,
      validLeads,
    },
  });

  useEffect(() => {
    if (isRecordingConsentRequired) setShowConsentModal(true);
  }, [isRecordingConsentRequired]);

  useEffect(() => {
    actions.setCallParams({ leadUuids, callSource: "Person Detail Page" });
  }, [leadUuids]);

  useEffect(
    () => () => {
      disconnectDialer();
      actions.setCallParams();
    },
    [],
  );

  return (
    <div className="tw-relative tw-flex tw-flex-col tw-gap-[24px]">
      <PlatformMessage />

      <div className="tw-flex tw-justify-between tw-items-end">
        <SinglePerson
          loading={loading}
          callState={callState}
          person={person}
          phoneNumbers={phoneNumbers}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumberInline}
        />
        <CallButton callState={callState} phoneNumber={phoneNumber} setCallState={setCallStateInline} />
      </div>
      <CallBar
        callState={callState}
        isRecording={isRecording}
        conference={conference}
        startRecording={strtRec}
        stopRecording={stpRec}
        setDuration={setDurationInline}
      />
      <CallOutcome callOutcome={callOutcome} setCallOutcome={setCallOutcomeInline} />
      <CallNotes
        isDnc={phoneNumber?.meta.isDnc}
        mentionableIdOrUuid={person?.uuid}
        mentionableType="Person"
        note={note}
        setComment={setCommentInline}
      />

      {error && <Alert variant="error" text={error} />}

      <div className="tw-flex tw-justify-between">
        <Button
          schema="tertiary"
          size="medium"
          onClick={() => {
            actions.setCallOutcome(null);
            actions.setComment("");
          }}
        >
          Clear
        </Button>
        <Button
          size="medium"
          onClick={() => actions.logCall()}
          disabled={!DISABLED_CALL_STATES.includes(callState) || !callOutcome}
        >
          Save & Log Call
        </Button>
      </div>

      {loading && <Loading />}

      <RecordConfirmationModal
        show={showConsentModal}
        closeModal={() => {
          actions.startRecording({ recordingConsent: false, isDismissed: true });
          setShowConsentModal(false);
        }}
        onClickRecord={() => {
          actions.startRecording({ recordingConsent: true, isDismissed: false });
          setShowConsentModal(false);
        }}
      />
    </div>
  );
};

SingleDialer.propTypes = {
  leadUuid: PropTypes.string,
};

SingleDialer.defaultProps = {
  leadUuid: null,
};

export default SingleDialer;
